/**
 * v2 Terminal Sessions list — recent + historical SSH sessions. Each
 * row links to a replay page (full command/response log).
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { Badge, type BadgeTone } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { TranscriptLines } from "../../components/TranscriptLines";
import { HelpButton } from "../../help/HelpPanel";

type SessionRow = {
  id: string;
  chassis_id: number | null;
  gigafy_device_name: string | null;
  actor_gigafy_username: string;
  state: "active" | "ended" | "expired";
  started_at: string;
  ended_at: string | null;
  command_count: number;
};

type ListResponse = { sessions: SessionRow[] };

const STATE_TONE: Record<string, BadgeTone> = {
  active: "info",
  ended: "muted",
  expired: "warning",
};

export function TerminalSessionsList() {
  const q = useQuery<ListResponse>({
    queryKey: ["v2", "terminal-sessions"],
    queryFn: async () => {
      const res = await api.get<ListResponse>("/terminal-sessions?limit=100");
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const sessions = q.data?.sessions ?? [];
  return (
    <section className="flex flex-col gap-3">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">
            Terminal sessions
          </h1>
          <HelpButton topic="tools-terminal-sessions" />
        </div>
        <p className="text-xs text-slate-500">
          Active and historical SSH sessions opened from the
          per-chassis Terminal button.
        </p>
      </header>
      <div className="rounded-lg bg-slate-900 shadow-border p-4">
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : sessions.length === 0 ? (
          <p className="text-xs text-slate-500">No sessions logged yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">State</th>
                <th className="px-2 py-1">Chassis</th>
                <th className="px-2 py-1">Actor</th>
                <th className="px-2 py-1">Commands</th>
                <th className="px-2 py-1">Started</th>
                <th className="px-2 py-1">Ended</th>
                <th className="px-2 py-1 text-right">Replay</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td className="px-2 py-1.5">
                    <Badge tone={STATE_TONE[s.state] ?? "neutral"}>
                      {s.state}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5">
                    {s.chassis_id ? (
                      <Link
                        to={`/chassis/${s.chassis_id}`}
                        className="text-emerald-400 hover:text-emerald-300"
                      >
                        {s.gigafy_device_name ?? `chassis-${s.chassis_id}`}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-2 py-1.5">{s.actor_gigafy_username}</td>
                  <td className="px-2 py-1.5 font-mono">{s.command_count}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={s.started_at} />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">
                    <LocalTime iso={s.ended_at} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Link
                      to={`/tools/terminal-sessions/${s.id}`}
                      className="text-emerald-400 hover:text-emerald-300"
                    >
                      open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

/** Session replay — full command/response log for one session. */
export function TerminalSessionDetail({ sid }: { sid: string }) {
  type Row = {
    id: number;
    ts: string;
    direction: string;
    content: string;
  };
  const q = useQuery<{ session: SessionRow; rows: Row[] }>({
    queryKey: ["v2", "terminal-sessions", sid],
    queryFn: async () => {
      const res = await api.get<{ session: SessionRow; rows: Row[] }>(
        `/terminal-sessions/${sid}`,
      );
      return res.data;
    },
  });
  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between">
        <h1 className="text-lg font-semibold text-slate-100">
          Session replay
        </h1>
        <HelpButton topic="tools-terminal-sessions" />
        <CopyableCell value={sid} />
      </header>
      <div className="rounded-lg bg-slate-900 shadow-border p-4">
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : (
          <TranscriptLines
            rows={q.data?.rows ?? []}
            maxHeightClass="max-h-[75vh]"
          />
        )}
      </div>
    </section>
  );
}
